import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2,
  ArrowRight,
  Package,
  FileText,
  Truck,
  FileSignature,
  Clock,
  Search,
} from "lucide-react";

export default function AdminClientesPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/clients");
        setClients(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q) return clients;
    const s = q.toLowerCase();
    return clients.filter(
      (c) =>
        c.email.toLowerCase().includes(s) ||
        c.name?.toLowerCase().includes(s) ||
        c.razon_social?.toLowerCase().includes(s) ||
        c.rut?.toLowerCase().includes(s) ||
        c.id_cliente?.toLowerCase().includes(s)
    );
  }, [clients, q]);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración · Vista por Cliente"
          title="Clientes"
          description="Selecciona una empresa cliente para ver y gestionar todas sus operaciones: envíos, SAP, entregas (RDA), cedibles y consolidado."
        />

        <div className="mb-6 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por empresa, RUT, ID_Cliente, email..."
            data-testid="clientes-search"
            className="w-full pl-10 pr-3 py-3 bg-white border border-slate-300 focus:border-[#D95A40] focus:ring-2 focus:ring-[#D95A40]/20 focus:outline-none text-sm transition-colors"
          />
        </div>

        {loading && (
          <div className="bg-white border border-slate-200 p-10 text-center text-slate-500">
            Cargando clientes...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 p-12 text-center">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500">
              {q
                ? "Sin resultados para tu búsqueda."
                : "No hay clientes registrados aún."}
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <Link
              key={c.id}
              to={`/admin/clientes/${c.id}`}
              data-testid={`cliente-card-${c.id}`}
              className="bg-white border border-slate-200 hover:border-[#0B1B3D] transition-all p-5 group flex flex-col"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-[#0B1B3D] text-white flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[#0B1B3D] truncate">
                      {c.razon_social || c.name}
                    </div>
                    <div className="text-xs text-slate-500 font-mono truncate">
                      {c.rut || "sin RUT"}
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#D95A40] group-hover:translate-x-1 transition-all shrink-0 mt-1" />
              </div>

              <div className="text-xs text-slate-600 mb-4 font-mono truncate">
                {c.email}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {c.id_cliente && (
                  <span className="text-[10px] uppercase tracking-wider bg-[#D95A40]/10 text-[#D95A40] font-bold px-2 py-0.5">
                    {c.id_cliente}
                  </span>
                )}
                {c.default_responsable && (
                  <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 font-bold px-2 py-0.5">
                    {c.default_responsable}
                  </span>
                )}
                {!c.active && (
                  <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 font-bold px-2 py-0.5">
                    Inactivo
                  </span>
                )}
              </div>

              <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-100 mt-auto">
                <Metric
                  icon={<Package className="w-3 h-3" />}
                  value={c.batches_total}
                  label="Envíos"
                />
                <Metric
                  icon={<FileText className="w-3 h-3" />}
                  value={c.docs_total}
                  label="Docs"
                />
                <Metric
                  icon={<Truck className="w-3 h-3" />}
                  value={c.rda_count}
                  label="RDAs"
                />
                <Metric
                  icon={<FileSignature className="w-3 h-3" />}
                  value={c.cedible_count}
                  label="Ced"
                />
              </div>

              {c.batches_pending > 0 && (
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-[#D95A40]">
                  <Clock className="w-3 h-3" />
                  <span className="font-bold">
                    {c.batches_pending} envío(s) pendiente(s) de SAP
                  </span>
                </div>
              )}
              {c.last_activity_at && (
                <div className="mt-2 text-[10px] text-slate-400">
                  Última actividad:{" "}
                  {new Date(c.last_activity_at).toLocaleDateString("es-CL")}
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function Metric({ icon, value, label }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-slate-400 mb-1">
        {icon}
        <span className="text-[9px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-display font-black text-xl text-[#0B1B3D] tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}
